import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Botao, FormSection, Icone, Modal } from '../../ui';
import { uploadArquivos } from '../../api/upload/uploadApi';

/**
 * Captura das fotos de documento — **um componente para as duas telas** de
 * `/documento*` (at-60, frente C/C3).
 *
 * Medido antes da reescrita: `documento.tsx` e `documentoEdit.tsx` declaravam a
 * MESMA câmera (os mesmos `startCamera`/`stopCamera`/`capturePhoto`/
 * `dataURLtoFile`/`removerImagem`), a MESMA prévia e o MESMO `enviarDocumentos`
 * — ~120 linhas idênticas em cada. O que difere entre as telas é só o payload
 * que cada uma monta (cadastro × atualização), e isso **fica nelas**.
 *
 * 🔴 Mora em `components/` e não em `src/ui` porque conhece o domínio: chama a
 * API de upload e sabe que existem "documentos do associado" e "do veículo". O
 * kit que ele usa é puro — mesmo precedente do `FormularioAssociado` (at-58) e
 * do `FormularioVeiculo` (at-59).
 *
 * ⚠️ **O envio não lança, de propósito.** Quando ele roda, o cadastro (ou a
 * atualização) **já aconteceu** e não pode ser desfeito por causa de uma foto —
 * então a falha vira aviso em voz alta para o consultor reenviar pela edição,
 * nunca uma exceção que derrube a tela (contrato herdado da at-38).
 */

/** O que a tela pode pedir a este componente. */
export interface HandleCapturaDocumentos {
  /**
   * Sobe o que foi capturado, JÁ com dono (at-38, achado 1 daquele plano).
   *
   * ⚠️ Antes da at-38 o upload rodava **antes** do cadastro e sem ids: toda
   * `Imagem` nascia órfã (`associadoId`/`carroId` nulos) e o retorno do
   * `Promise.all` nem era lido, então falha de upload passava em silêncio.
   */
  enviarDocumentos: (associadoId: string, carroId?: string) => Promise<void>;
}

interface PropsCapturaDocumentos {
  /**
   * Como a mensagem de falha de upload começa — a única diferença de texto
   * entre as duas telas: "O cadastro foi feito" × "A atualização foi salva".
   */
  prefixoFalhaUpload: string;
  /** Trava a captura enquanto a tela está gravando. */
  desabilitado?: boolean;
}

type Grupo = 'associado' | 'veiculo';

const GRUPOS: ReadonlyArray<{ chave: Grupo; titulo: string; tipo: string }> = [
  { chave: 'associado', titulo: 'Documentos do Associado', tipo: 'Documento do Associado' },
  { chave: 'veiculo', titulo: 'Documentos do Veículo', tipo: 'Documento do Veículo' },
];

function dataUrlParaArquivo(dataUrl: string, nome: string): File {
  const partes = dataUrl.split(',');
  const mime = partes[0].match(/:(.*?);/)?.[1] || 'image/png';
  const binario = atob(partes[1]);
  let n = binario.length;
  const bytes = new Uint8Array(n);
  while (n--) bytes[n] = binario.charCodeAt(n);
  return new File([bytes], nome, { type: mime });
}

const CapturaDocumentos = forwardRef<HandleCapturaDocumentos, PropsCapturaDocumentos>(
  ({ prefixoFalhaUpload, desabilitado = false }, ref) => {
    const video = useRef<HTMLVideoElement | null>(null);
    const canvas = useRef<HTMLCanvasElement | null>(null);

    const [fotos, setFotos] = useState<Record<Grupo, string[]>>({ associado: [], veiculo: [] });
    const [cameraAberta, setCameraAberta] = useState<Grupo | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);

    const abrirCamera = async (grupo: Grupo) => {
      try {
        const novoStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        setStream(novoStream);
        setCameraAberta(grupo);

        /*
         * ⚠️ O `setTimeout` é o mecanismo ORIGINAL de ligar o stream ao `<video>`
         * e foi preservado de propósito: o elemento só existe depois de o React
         * renderizar o diálogo, e câmera é a parte do fluxo que não se consegue
         * exercitar em teste automatizado. Trocá-lo por um efeito seria mexer no
         * único trecho desta tela que não dá para provar aqui.
         */
        setTimeout(() => {
          if (video.current) {
            video.current.srcObject = novoStream;
            video.current.setAttribute('playsinline', 'true');
            void video.current.play();
          }
        }, 100);
      } catch {
        // Falha nunca é muda (§4.4 do plano). Era um `alert()` nativo, que não
        // respeita a identidade visual e bloqueia a thread.
        toast.error('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
      }
    };

    const fecharCamera = () => {
      stream?.getTracks().forEach((faixa) => faixa.stop());
      setStream(null);
      setCameraAberta(null);
    };

    const capturar = () => {
      if (!video.current || !canvas.current || !cameraAberta) return;

      const contexto = canvas.current.getContext('2d');
      if (!contexto) return;

      contexto.drawImage(video.current, 0, 0, canvas.current.width, canvas.current.height);
      const imagem = canvas.current.toDataURL('image/png');

      setFotos((anterior) => ({ ...anterior, [cameraAberta]: [...anterior[cameraAberta], imagem] }));
      fecharCamera();
    };

    const remover = (grupo: Grupo, indice: number) =>
      setFotos((anterior) => ({
        ...anterior,
        [grupo]: anterior[grupo].filter((_, i) => i !== indice),
      }));

    useImperativeHandle(ref, () => ({
      enviarDocumentos: async (associadoId: string, carroId?: string) => {
        for (const grupo of GRUPOS) {
          const arquivos = fotos[grupo.chave].map((imagem, indice) =>
            dataUrlParaArquivo(imagem, `${grupo.chave}-${Date.now()}-${indice}.png`),
          );
          if (arquivos.length === 0) continue;

          try {
            await uploadArquivos(arquivos, grupo.tipo, associadoId, carroId);
          } catch {
            toast.warn(
              `${prefixoFalhaUpload}, mas os documentos do ${grupo.chave} não foram enviados. ` +
                'Reenvie pela edição do cadastro.',
              { autoClose: 8000 },
            );
          }
        }
      },
    }));

    const tituloDaCamera = GRUPOS.find((g) => g.chave === cameraAberta)?.titulo ?? '';

    return (
      <>
        {GRUPOS.map((grupo) => (
          <FormSection key={grupo.chave} titulo={grupo.titulo} icone="identificacao">
            <Botao
              variante="secundario"
              iconeEsquerda="mais"
              larguraTotal
              disabled={desabilitado}
              onClick={() => void abrirCamera(grupo.chave)}
            >
              Tirar foto
            </Botao>

            {fotos[grupo.chave].length === 0 ? (
              <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
                Nenhuma foto capturada. As fotos são enviadas ao concluir esta etapa.
              </p>
            ) : (
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-3 tw-p-0">
                {fotos[grupo.chave].map((imagem, indice) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${grupo.chave}-${indice}`} className="tw-relative">
                    <img
                      src={imagem}
                      alt={`${grupo.titulo} — foto ${indice + 1}`}
                      className="tw-h-24 tw-w-24 tw-rounded-xl tw-border tw-border-superficie-borda tw-object-cover"
                    />
                    {/*
                      ⚠️ Era um "✖" literal. A regra de Victor de 06/09/2026 é
                      **emoji nunca na interface**, e as `03-code-conventions`
                      nomeiam este botão como um dos dois resíduos a trocar ao
                      tocar no trecho — é o que se faz aqui.
                    */}
                    <button
                      type="button"
                      onClick={() => remover(grupo.chave, indice)}
                      disabled={desabilitado}
                      aria-label={`Remover a foto ${indice + 1} de ${grupo.titulo}`}
                      className="tw-absolute tw--right-2 tw--top-2 tw-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-danger tw-text-white tw-shadow-[0_1px_4px_rgba(0,0,0,0.3)] hover:tw-brightness-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                    >
                      <Icone nome="fechar" tamanho={14} traco={2.5} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </FormSection>
        ))}

        {/*
          A câmera passou a viver num diálogo do kit. Antes era um bloco INLINE
          empurrando o resto da tela para baixo — e no `assinatura.tsx` o mesmo
          padrão tentava ser um modal com classes `fixed inset-0 …` **sem o
          prefixo `tw-`**, ou seja, sem efeito nenhum.
        */}
        <Modal
          aberto={cameraAberta !== null}
          titulo={tituloDaCamera}
          descricao="Enquadre o documento e toque em Capturar."
          aoFechar={fecharCamera}
          rodape={
            <>
              <Botao variante="primario" onClick={capturar}>
                Capturar
              </Botao>
              <Botao variante="secundario" onClick={fecharCamera}>
                Cancelar
              </Botao>
            </>
          }
        >
          <video
            ref={video}
            autoPlay
            playsInline
            muted
            className="tw-w-full tw-rounded-xl tw-bg-black"
          />
        </Modal>

        {/* Fora do diálogo: é a superfície de desenho do frame, nunca visível. */}
        <canvas ref={canvas} width={400} height={300} className="tw-hidden" />
      </>
    );
  },
);

CapturaDocumentos.displayName = 'CapturaDocumentos';

export default CapturaDocumentos;
